import React, { useEffect } from 'react';
import { match } from 'ts-pattern';
import { useAccount, useConfig } from 'wagmi';
import { SchemaSignerDto } from '../../../generated/apiTypes';
import { useActiveSigner, useLinkedSigners } from '../../../hooks/useActiveSigner';
import { useDataForSigning } from '../../../hooks/useDataForSigning';
import { useActiveSelectedSubaccount } from '../../../hooks/useSelectedSubaccount';
import { isUserRejectedError } from '../../../lib/errors';
import { isExpired } from '../../../lib/signer';
import { assertRequiredData } from '../../../lib/typeGuards';
import { Alert } from '../../Alert';
import { Button } from '../../catalyst-ui-kit/button';
import { Loader } from '../../Loader';
import { OneClickStateBadge } from '../OneClickStateBadge';
import { useSettingsState } from '../SettingsState';
import { ALERT_CONTENT } from '../signerAlertContent';
import { useNewSigner } from '../signerHooks';

const SettingsOneClickPanelUi: React.FC<{
  hasSubaccount: boolean;
  hasRequiredDataErrors: boolean;
  requiredDataPending: boolean;
  onEnableClick: () => void;
  onDisableClick: () => void;
  onManageClick: () => void;
  onRenewClick: () => void;
  signer: SchemaSignerDto | undefined;
  enableMutation: ReturnType<typeof useNewSigner>;
  linkedSigners: SchemaSignerDto[] | undefined;
  loadingLinkedSigners: boolean;
}> = ({
  hasSubaccount,
  onEnableClick,
  onDisableClick,
  onManageClick,
  onRenewClick,
  hasRequiredDataErrors,
  requiredDataPending,
  signer,
  enableMutation,
  linkedSigners,
  loadingLinkedSigners,
}) => {
  const expired = isExpired(signer);

  const alert = (() => {
    switch (true) {
      case hasRequiredDataErrors && !requiredDataPending: {
        return ALERT_CONTENT.REQUIRED_DATA_ERROR;
      }

      case enableMutation.isSuccess: {
        return ALERT_CONTENT.ENABLED_SUCCESS;
      }

      case enableMutation.error && !isUserRejectedError(enableMutation.error): {
        return {
          variant: ALERT_CONTENT.ENABLE_ERROR.variant,
          message: ALERT_CONTENT.ENABLE_ERROR.getMessage(enableMutation.error),
        };
      }
      case enableMutation.isPending: {
        return ALERT_CONTENT.ENABLE_PENDING;
      }

      default: {
        return null;
      }
    }
  })();

  return (
    <div className="flex flex-col gap-4 sm:p-4">
      <div className="flex items-center justify-between text-xs font-medium text-contentSecondary">
        <span>One-Click Trading</span>
        <OneClickStateBadge />
      </div>
      <p className="text-xs font-medium text-contentSecondary">
        {signer
          ? 'Disabling One-click trading won’t stop you from trading—it just means you’ll need to sign each trade manually.'
          : `Experience effortless trading with One-Click Trading. Sign one approval transaction at the
 start of your trading session and you won't need to sign again.`}
      </p>
      <div className="flex items-center justify-between text-xs font-medium text-contentSecondary">
        <span>
          One-Click Trading Sessions{' '}
          {loadingLinkedSigners ? (
            <span className="h-5 w-6 animate-pulse rounded bg-backgroundTertiary"></span>
          ) : (
            <span>
              (<span className="text-contentPrimary">{linkedSigners?.length ?? 0}</span>)
            </span>
          )}
        </span>
        <Button
          onClick={onManageClick}
          outline
          disabled={!linkedSigners || linkedSigners.length === 0}
          className="!rounded !px-2 !py-[2px] !text-xs"
        >
          Manage
        </Button>
      </div>
      {alert && <Alert variant={alert.variant}>{alert.message}</Alert>}
      {match({ signer, expired })
        .with({ expired: true }, () => {
          return (
            <div className="grid grid-cols-2 gap-2">
              <Button color="blue" onClick={onRenewClick}>
                Renew
              </Button>
              <Button outline onClick={onDisableClick}>
                Disable
              </Button>
            </div>
          );
        })
        .with({ signer: undefined }, () => {
          return (
            <Button
              outline
              disabled={Boolean(
                !hasSubaccount ||
                  requiredDataPending ||
                  hasRequiredDataErrors ||
                  enableMutation.isPending
              )}
              onClick={onEnableClick}
              className="mt-2 flex items-center justify-center gap-1"
            >
              {enableMutation.isPending ? <Loader /> : null}
              Enable One-Click Trading
            </Button>
          );
        })
        .otherwise(() => {
          return (
            <Button outline onClick={onDisableClick}>
              Disable One-Click Trading
            </Button>
          );
        })}
    </div>
  );
};

export const SettingsOneClickPanel: React.FC = () => {
  const { address } = useAccount();
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { data: activeSigner } = useActiveSigner();
  const config = useConfig();
  const {
    domain,
    signedTypedDataTypes,
    error: requiredDataError,
    isPending: requiredDataPending,
  } = useDataForSigning();
  const hasRequiredDataErrors = Boolean(requiredDataError);
  const enableMutation = useNewSigner('enable');

  const pushPanel = useSettingsState(({ pushPanel }) => pushPanel);
  const setSignerToRevoke = useSettingsState(({ setSignerToRevoke }) => setSignerToRevoke);
  const currentPanel = useSettingsState((state) => state.panelStack.at(-1));

  const { isPending, reset: enableReset } = enableMutation;

  useEffect(() => {
    // Reset if the not currently showing one-click panel
    if (currentPanel !== 'one-click') {
      enableReset();
    }
  }, [isPending, enableReset, currentPanel]);

  const mutationParams = {
    subaccountName: selectedSubaccount?.name,
    subaccountId: selectedSubaccount?.id,
    address,
    domain,
    signedTypedDataTypes,
    config,
  };

  const { data: linkedSigners, isLoading: loadingLinkedSigners } = useLinkedSigners();

  return (
    <SettingsOneClickPanelUi
      hasSubaccount={Boolean(address && selectedSubaccount)}
      signer={activeSigner}
      onRenewClick={() => {
        if (!activeSigner) {
          return;
        }

        setSignerToRevoke(activeSigner.signer);
        pushPanel('renew');
      }}
      onEnableClick={() => {
        assertRequiredData(mutationParams);
        enableMutation.mutate(mutationParams);
      }}
      onDisableClick={() => {
        if (activeSigner?.signer) {
          setSignerToRevoke(activeSigner?.signer);
        }
        pushPanel('disable-one-click');
      }}
      onManageClick={() => pushPanel('manage-sessions')}
      enableMutation={enableMutation}
      hasRequiredDataErrors={hasRequiredDataErrors}
      requiredDataPending={requiredDataPending}
      linkedSigners={linkedSigners}
      loadingLinkedSigners={loadingLinkedSigners}
    />
  );
};
